import type { MonitoringScope } from '../types';
/**
 * Post Investigation Page — Rich media, platform-specific cards, full-screen modal
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, X, ExternalLink, AlertTriangle, TrendingUp,
  MessageCircle, ThumbsUp, Eye, Clock, User, Globe, Film, Mic,
  FileText, Image, ChevronRight, ZoomIn, Flag, CheckCircle,
  Twitter, Youtube, Hash, Radio, Newspaper, Activity,
  Layers, Share2, BarChart2, Link as LinkIcon, Download,
} from 'lucide-react';
import { Post, RiskLevel } from '../types';
import apiService from '../services/apiService';

// ─── Platform config ──────────────────────────────────────────────────────────
const PLATFORM_CONFIG: Record<string, {
  label: string; color: string; bg: string; border: string;
  icon: React.ReactNode; headerGrad: string;
}> = {
  twitter: {
    label: 'Twitter / X', color: 'text-sky-400', bg: 'bg-sky-400/10',
    border: 'border-sky-400/30', headerGrad: 'from-sky-900/60 to-zinc-900',
    icon: <Twitter className="w-4 h-4" />,
  },
  reddit: {
    label: 'Reddit', color: 'text-orange-400', bg: 'bg-orange-400/10',
    border: 'border-orange-400/30', headerGrad: 'from-orange-900/60 to-zinc-900',
    icon: <Hash className="w-4 h-4" />,
  },
  youtube: {
    label: 'YouTube', color: 'text-rose-400', bg: 'bg-rose-400/10',
    border: 'border-rose-400/30', headerGrad: 'from-rose-900/60 to-zinc-900',
    icon: <Youtube className="w-4 h-4" />,
  },
  facebook: {
    label: 'Facebook', color: 'text-blue-400', bg: 'bg-blue-400/10',
    border: 'border-blue-400/30', headerGrad: 'from-blue-900/60 to-zinc-900',
    icon: <Globe className="w-4 h-4" />,
  },
  telegram: {
    label: 'Telegram', color: 'text-cyan-400', bg: 'bg-cyan-400/10',
    border: 'border-cyan-400/30', headerGrad: 'from-cyan-900/60 to-zinc-900',
    icon: <Radio className="w-4 h-4" />,
  },
  mastodon: {
    label: 'Mastodon', color: 'text-violet-400', bg: 'bg-violet-400/10',
    border: 'border-violet-400/30', headerGrad: 'from-violet-900/60 to-zinc-900',
    icon: <Activity className="w-4 h-4" />,
  },
  news: {
    label: 'News', color: 'text-emerald-400', bg: 'bg-emerald-400/10',
    border: 'border-emerald-400/30', headerGrad: 'from-emerald-900/60 to-zinc-900',
    icon: <Newspaper className="w-4 h-4" />,
  },
};

const getPlatform = (p: string) =>
  PLATFORM_CONFIG[p?.toLowerCase()] ?? {
    label: p?.toUpperCase() || 'UNKNOWN', color: 'text-zinc-400',
    bg: 'bg-zinc-400/10', border: 'border-zinc-400/30',
    headerGrad: 'from-zinc-800 to-zinc-900', icon: <Globe className="w-4 h-4" />,
  };

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getRiskLevel = (score: number): RiskLevel => {
  if (score > 0.8) return 'CRITICAL';
  if (score > 0.6) return 'HIGH';
  if (score > 0.4) return 'MEDIUM';
  if (score > 0.2) return 'LOW';
  return 'MINIMAL';
};

const RISK_COLORS: Record<RiskLevel, string> = {
  MINIMAL: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
  LOW: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
  MEDIUM: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
  HIGH: 'bg-orange-500/10 text-orange-400 border-orange-500/30',
  CRITICAL: 'bg-rose-500/10 text-rose-400 border-rose-500/30',
};

const RISK_BAR: Record<RiskLevel, string> = {
  MINIMAL: 'bg-emerald-500', LOW: 'bg-blue-500', MEDIUM: 'bg-amber-500',
  HIGH: 'bg-orange-500', CRITICAL: 'bg-rose-500',
};

const detectMediaType = (post: any): 'video' | 'audio' | 'image' | 'text' => {
  const url = (post.url || '').toLowerCase();
  const text = (post.text || '').toLowerCase();
  if (post.platform === 'youtube' || url.includes('youtube.com') || url.includes('youtu.be')) return 'video';
  if (url.match(/\.(mp4|webm|mov|avi)/) || text.includes('[video]')) return 'video';
  if (url.match(/\.(mp3|wav|ogg|m4a)/) || text.includes('[audio]')) return 'audio';
  if (url.match(/\.(jpg|jpeg|png|gif|webp)/) || post.image_url) return 'image';
  return 'text';
};

const extractYouTubeId = (url: string): string | null => {
  const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
};

const initials = (name: string) =>
  name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '??';

const MOCK_POSTS: Post[] = [
  { id: 'm1', platform: 'twitter', text: 'Breaking: Election voting machines compromised in 3 states — unverified claims spreading rapidly across social media. Officials deny reports.', authorName: '@ElectionWatch', authorId: 'u1', riskScore: 0.88, riskLevel: 'CRITICAL', rumourProbability: 0.91, sentiment: 'NEGATIVE', sentimentScore: -0.75, botScore: 0.72, engagement: 14200, postedAt: new Date(Date.now() - 3600000).toISOString(), tags: ['election', 'voting', 'misinformation'], url: 'https://twitter.com/status/example1' },
  { id: 'm2', platform: 'youtube', text: 'AI-Generated deepfake video of minister making false statements goes viral — 2.1M views in 6 hours. Fact-checkers flag as synthetic media.', authorName: 'TruthChannel India', authorId: 'u2', riskScore: 0.95, riskLevel: 'CRITICAL', rumourProbability: 0.88, sentiment: 'NEGATIVE', sentimentScore: -0.81, botScore: 0.34, engagement: 2100000, postedAt: new Date(Date.now() - 7200000).toISOString(), tags: ['deepfake', 'AI', 'political'], url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ' },
  { id: 'm3', platform: 'reddit', text: 'r/India: Thread claiming new government policy will freeze all UPI transactions — no official confirmation. Post gaining traction despite no credible sources.', authorName: 'u/IndiaWatcher', authorId: 'u3', riskScore: 0.61, riskLevel: 'HIGH', rumourProbability: 0.77, sentiment: 'NEGATIVE', sentimentScore: -0.55, botScore: 0.12, engagement: 4870, postedAt: new Date(Date.now() - 10800000).toISOString(), tags: ['UPI', 'India', 'policy', 'rumor'] },
  { id: 'm4', platform: 'telegram', text: 'URGENT FORWARD: New COVID variant OMEGA-X confirmed by WHO — all schools to close. [NO SUCH WHO ANNOUNCEMENT EXISTS]', authorName: 'HealthAlerts_IN', authorId: 'u4', riskScore: 0.82, riskLevel: 'CRITICAL', rumourProbability: 0.94, sentiment: 'NEGATIVE', sentimentScore: -0.88, botScore: 0.58, engagement: 31000, postedAt: new Date(Date.now() - 1800000).toISOString(), tags: ['covid', 'health', 'WHO', 'fake'] },
  { id: 'm5', platform: 'news', text: 'NDTV Fact Check: Viral image claiming to show flooding in Delhi is from 2021 Bangladesh floods. Original photo traced to Dhaka.', authorName: 'NDTV Fact Check', authorId: 'u5', riskScore: 0.38, riskLevel: 'MEDIUM', rumourProbability: 0.42, sentiment: 'NEUTRAL', sentimentScore: 0.05, botScore: 0.04, engagement: 9200, postedAt: new Date(Date.now() - 5400000).toISOString(), tags: ['factcheck', 'Delhi', 'flood', 'image'], url: 'https://ndtv.com/factcheck/example', },
];

// ─── Media renderer ───────────────────────────────────────────────────────────
const MediaBlock: React.FC<{ post: any }> = ({ post }) => {
  const mediaType = detectMediaType(post);
  const ytId = post.url ? extractYouTubeId(post.url) : null;

  if (mediaType === 'video' && ytId) {
    return (
      <div className="rounded-xl overflow-hidden border border-zinc-700/50 mb-4">
        <div className="flex items-center gap-2 px-3 py-2 bg-rose-500/10 border-b border-zinc-700/40">
          <Film className="w-3.5 h-3.5 text-rose-400" />
          <span className="text-xs font-bold text-rose-400 uppercase tracking-wide">YouTube Video</span>
        </div>
        <div className="relative aspect-video bg-zinc-900">
          <iframe
            src={`https://www.youtube.com/embed/${ytId}?rel=0&modestbranding=1`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 w-full h-full"
            title="Post video"
          />
        </div>
      </div>
    );
  }

  if (mediaType === 'video') {
    return (
      <div className="flex items-center gap-3 p-3 bg-rose-500/5 border border-rose-500/20 rounded-xl mb-4">
        <div className="p-2 bg-rose-500/15 rounded-lg"><Film className="w-5 h-5 text-rose-400" /></div>
        <div>
          <p className="text-sm font-semibold text-rose-300">Video Content</p>
          <p className="text-xs text-zinc-400">Video post detected — source platform required to embed</p>
        </div>
        {post.url && (
          <a href={post.url} target="_blank" rel="noopener noreferrer"
            className="ml-auto px-3 py-1.5 text-xs font-bold bg-rose-500/15 text-rose-400 rounded-lg hover:bg-rose-500/25 transition-colors flex items-center gap-1">
            Open <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>
    );
  }

  if (mediaType === 'audio') {
    return (
      <div className="flex items-center gap-3 p-3 bg-violet-500/5 border border-violet-500/20 rounded-xl mb-4">
        <div className="p-2 bg-violet-500/15 rounded-lg"><Mic className="w-5 h-5 text-violet-400" /></div>
        <div>
          <p className="text-sm font-semibold text-violet-300">Audio Content</p>
          <p className="text-xs text-zinc-400">Audio/podcast post detected</p>
        </div>
        {post.url && (
          <a href={post.url} target="_blank" rel="noopener noreferrer"
            className="ml-auto px-3 py-1.5 text-xs font-bold bg-violet-500/15 text-violet-400 rounded-lg hover:bg-violet-500/25 transition-colors flex items-center gap-1">
            Listen <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>
    );
  }

  if (mediaType === 'image' && (post.image_url || post.url?.match(/\.(jpg|jpeg|png|gif|webp)/))) {
    const imgSrc = post.image_url || post.url;
    return (
      <div className="rounded-xl overflow-hidden border border-zinc-700/50 mb-4">
        <div className="flex items-center gap-2 px-3 py-2 bg-cyan-500/10 border-b border-zinc-700/40">
          <Image className="w-3.5 h-3.5 text-cyan-400" />
          <span className="text-xs font-bold text-cyan-400 uppercase tracking-wide">Image Attachment</span>
        </div>
        <img src={imgSrc} alt="Post media" className="w-full max-h-64 object-cover" onError={e => (e.currentTarget.style.display = 'none')} />
      </div>
    );
  }

  // Text — show type badge only
  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-zinc-800/40 border border-zinc-700/30 rounded-xl mb-4">
      <FileText className="w-3.5 h-3.5 text-zinc-400" />
      <span className="text-xs text-zinc-400 font-medium">Text Post</span>
    </div>
  );
};

// ─── Full Investigation Modal ─────────────────────────────────────────────────
const InvestigationModal: React.FC<{ post: any; onClose: () => void }> = ({ post, onClose }) => {
  const plat = getPlatform(post.platform);
  const mediaType = detectMediaType(post);
  const [activeTab, setActiveTab] = useState<'overview' | 'cross-channel' | 'spread'>('overview');
  const [crossData, setCrossData] = useState<any>(null);
  const [spreadData, setSpreadData] = useState<any>(null);
  const [crossLoading, setCrossLoading] = useState(false);
  const [spreadLoading, setSpreadLoading] = useState(false);

  useEffect(() => {
    if (!post.id || post.id.toString().startsWith('m')) return; // skip mock posts
    setCrossLoading(true);
    apiService.posts.getCrossChannel(post.id)
      .then(setCrossData)
      .catch(() => setCrossData(null))
      .finally(() => setCrossLoading(false));
    setSpreadLoading(true);
    apiService.posts.getSpreadAnalysis(post.id)
      .then(setSpreadData)
      .catch(() => setSpreadData(null))
      .finally(() => setSpreadLoading(false));
  }, [post.id]);

  const scoreBar = (value: number, color: string, label: string, sublabel?: string) => (
    <div>
      <div className="flex justify-between items-center mb-1.5">
        <div>
          <span className="text-sm font-medium text-zinc-300">{label}</span>
          {sublabel && <span className="text-xs text-zinc-400 ml-2">{sublabel}</span>}
        </div>
        <span className="text-base font-bold text-white">{(value * 100).toFixed(1)}%</span>
      </div>
      <div className="w-full bg-zinc-800 rounded-full h-2.5">
        <motion.div
          className={`h-2.5 rounded-full ${color}`}
          initial={{ width: 0 }}
          animate={{ width: `${value * 100}%` }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        />
      </div>
    </div>
  );

  // ── Cross-Channel Tab ──────────────────────────────────────────────────────
  const CrossChannelPanel = () => {
    if (crossLoading) return (
      <div className="flex flex-col items-center justify-center py-20 text-zinc-400">
        <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-sm">Scanning all platforms for matching content…</p>
      </div>
    );
    if (!crossData) return (
      <div className="flex flex-col items-center justify-center py-20 text-zinc-400">
        <Layers className="w-10 h-10 mb-3 opacity-30" />
        <p className="text-sm">Cross-channel data unavailable for this post.</p>
        <p className="text-xs mt-1 text-zinc-400">Only real posts from the database can be investigated cross-channel.</p>
      </div>
    );
    const { channels = [], timeline = [], total_mentions, platform_count, keywords_used = [] } = crossData;

    // Timeline mini chart: compute max for scale
    const maxMentions = Math.max(...timeline.map((t: any) => t.mentions), 1);

    return (
      <div className="space-y-6 p-8">
        {/* Header stats */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'Total Mentions', value: total_mentions ?? 0, color: 'text-cyan-400', icon: <Layers className="w-5 h-5" /> },
            { label: 'Platforms', value: platform_count ?? 0, color: 'text-violet-400', icon: <Globe className="w-5 h-5" /> },
            { label: 'Keywords Used', value: keywords_used.length, color: 'text-amber-400', icon: <Search className="w-5 h-5" /> },
          ].map(s => (
            <div key={s.label} className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-4 flex gap-3 items-center">
              <div className={`${s.color} opacity-70`}>{s.icon}</div>
              <div>
                <p className={`text-2xl font-black ${s.color}`}>{s.value}</p>
                <p className="text-xs text-zinc-400">{s.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Keywords */}
        {keywords_used.length > 0 && (
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs text-zinc-400 uppercase tracking-wide font-bold">Search terms:</span>
            {keywords_used.map((kw: string) => (
              <span key={kw} className="text-xs px-2 py-1 bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 rounded-lg font-mono">{kw}</span>
            ))}
          </div>
        )}

        {/* Timeline mini-chart */}
        {timeline.length > 0 && (
          <div>
            <p className="text-xs font-bold text-zinc-400 uppercase tracking-widest mb-3 flex items-center gap-2">
              <BarChart2 className="w-3.5 h-3.5" /> Mention Timeline
            </p>
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-4">
              <div className="flex items-end gap-1 h-20 overflow-x-auto pb-1">
                {timeline.slice(-48).map((t: any, i: number) => (
                  <div key={i} className="flex flex-col items-center flex-shrink-0" style={{ minWidth: '12px' }}>
                    <div
                      className="w-2.5 rounded-t bg-cyan-500 hover:bg-cyan-400 transition-colors cursor-default"
                      style={{ height: `${(t.mentions / maxMentions) * 72}px` }}
                      title={`${t.hour}: ${t.mentions} mentions`}
                    />
                  </div>
                ))}
              </div>
              <p className="text-xs text-zinc-400 mt-2">Last {Math.min(timeline.length, 48)} hours of activity</p>
            </div>
          </div>
        )}

        {/* Platform cards */}
        {channels.length === 0 ? (
          <div className="text-center py-12 text-zinc-400">
            <Layers className="w-8 h-8 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No cross-platform mentions found for this story.</p>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-xs font-bold text-zinc-400 uppercase tracking-widest">Platform Breakdown</p>
            {channels.map((ch: any) => {
              const cp = getPlatform(ch.platform);
              return (
                <div key={ch.platform} className={`border ${cp.border} rounded-2xl overflow-hidden`}>
                  {/* Platform header */}
                  <div className={`flex items-center justify-between px-5 py-3 ${cp.bg} border-b ${cp.border}`}>
                    <div className="flex items-center gap-2">
                      <span className={cp.color}>{cp.icon}</span>
                      <span className={`font-bold text-sm ${cp.color}`}>{cp.label}</span>
                      {ch.has_images && <span className="text-xs px-1.5 py-0.5 bg-cyan-500/15 text-cyan-400 rounded border border-cyan-500/30">IMG</span>}
                      {ch.has_videos && <span className="text-xs px-1.5 py-0.5 bg-rose-500/15 text-rose-400 rounded border border-rose-500/30">VID</span>}
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span className={`font-black text-lg ${cp.color}`}>{ch.mention_count}</span>
                      <span className="text-zinc-400">mentions</span>
                      {ch.avg_risk >= 0.7 && <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-rose-500/15 text-rose-400 border border-rose-500/30">HIGH RISK</span>}
                    </div>
                  </div>
                  {/* Timestamps + samples */}
                  <div className="px-5 py-4 bg-zinc-900/30 space-y-3">
                    <div className="flex items-center gap-4 text-xs text-zinc-400">
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> First: {ch.first_seen ? new Date(ch.first_seen).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' }) : '—'}</span>
                      <span className="flex items-center gap-1"><Activity className="w-3 h-3" /> Latest: {ch.last_seen ? new Date(ch.last_seen).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' }) : '—'}</span>
                      <span className="flex items-center gap-1"><BarChart2 className="w-3 h-3" /> Avg Risk: {(ch.avg_risk * 100).toFixed(0)}%</span>
                    </div>
                    {/* Sample posts */}
                    {ch.sample_posts?.map((sp: any) => (
                      <div key={sp.id} className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-3 space-y-1.5">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-zinc-400 font-medium">{sp.author || 'Unknown'}</span>
                          <div className="flex items-center gap-2">
                            {sp.risk_score !== null && (
                              <span className={`text-xs px-1.5 py-0.5 rounded font-bold ${sp.risk_score >= 0.7 ? 'bg-rose-500/15 text-rose-400' : sp.risk_score >= 0.4 ? 'bg-amber-500/15 text-amber-400' : 'bg-emerald-500/15 text-emerald-400'}`}>
                                {(sp.risk_score * 100).toFixed(0)}% risk
                              </span>
                            )}
                            {sp.url && (
                              <a href={sp.url} target="_blank" rel="noopener noreferrer" className="text-zinc-400 hover:text-cyan-400 transition-colors">
                                <LinkIcon className="w-3.5 h-3.5" />
                              </a>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-zinc-400 leading-relaxed line-clamp-2">{sp.text}</p>
                        {sp.image_url && (
                          <img src={sp.image_url} alt="Post media" className="rounded-lg max-h-24 object-cover mt-1 border border-zinc-700/50" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // ── Spread Tracker Tab ─────────────────────────────────────────────────────
  const SpreadTrackerPanel = () => {
    if (spreadLoading) return (
      <div className="flex flex-col items-center justify-center py-20 text-zinc-400">
        <div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-sm">Calculating spread velocity…</p>
      </div>
    );
    if (!spreadData) return (
      <div className="flex flex-col items-center justify-center py-20 text-zinc-400">
        <Share2 className="w-10 h-10 mb-3 opacity-30" />
        <p className="text-sm">Spread data unavailable for this post.</p>
      </div>
    );

    const {
      spread_score = 0, velocity_per_hour = 0, total_related_posts = 0,
      platform_progression = [], risk_over_time = [], is_viral, spread_label,
      peak_hour,
    } = spreadData;

    const scoreColor = spread_score >= 80 ? 'text-rose-400' : spread_score >= 50 ? 'text-orange-400' : spread_score >= 25 ? 'text-amber-400' : 'text-emerald-400';
    const scoreBar2Color = spread_score >= 80 ? 'bg-rose-500' : spread_score >= 50 ? 'bg-orange-500' : spread_score >= 25 ? 'bg-amber-500' : 'bg-emerald-500';
    const labelColor = spread_score >= 80 ? 'bg-rose-500/10 text-rose-400 border-rose-500/30' : spread_score >= 50 ? 'bg-orange-500/10 text-orange-400 border-orange-500/30' : spread_score >= 25 ? 'bg-amber-500/10 text-amber-400 border-amber-500/30' : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30';

    const maxRisk = Math.max(...risk_over_time.map((r: any) => r.avg_risk), 0.1);

    return (
      <div className="space-y-6 p-8">
        {/* Spread score hero */}
        <div className={`rounded-2xl border p-6 flex items-center gap-6 ${spread_score >= 60 ? 'bg-rose-500/5 border-rose-500/30' : 'bg-zinc-900/50 border-zinc-800'}`}>
          <div className="text-center">
            <p className={`text-5xl font-black ${scoreColor}`}>{spread_score}</p>
            <p className="text-xs text-zinc-400 mt-1">Spread Score</p>
          </div>
          <div className="flex-1 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-zinc-300">Spread Index</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-black border ${labelColor}`}>{spread_label}</span>
            </div>
            <div className="w-full bg-zinc-800 rounded-full h-3">
              <motion.div
                className={`h-3 rounded-full ${scoreBar2Color}`}
                initial={{ width: 0 }}
                animate={{ width: `${spread_score}%` }}
                transition={{ duration: 1, ease: 'easeOut' }}
              />
            </div>
            {is_viral && (
              <div className="flex items-center gap-2 text-xs text-rose-300 font-semibold">
                <AlertTriangle className="w-3.5 h-3.5 text-rose-400" />
                This story is spreading virally across platforms
              </div>
            )}
          </div>
        </div>

        {/* Key metrics */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'Velocity', value: `${velocity_per_hour}/hr`, sub: 'Posts per hour', color: 'text-cyan-400', icon: <TrendingUp className="w-5 h-5" /> },
            { label: 'Total Related', value: total_related_posts, sub: 'Posts found', color: 'text-violet-400', icon: <FileText className="w-5 h-5" /> },
            { label: 'Peak Hour', value: peak_hour ? new Date(peak_hour).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—', sub: 'Highest activity', color: 'text-amber-400', icon: <Clock className="w-5 h-5" /> },
          ].map(s => (
            <div key={s.label} className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-4">
              <div className={`${s.color} opacity-70 mb-2`}>{s.icon}</div>
              <p className={`text-xl font-black ${s.color}`}>{s.value}</p>
              <p className="text-xs text-zinc-400 mt-0.5">{s.label}</p>
              <p className="text-xs text-zinc-700">{s.sub}</p>
            </div>
          ))}
        </div>

        {/* Platform progression */}
        {platform_progression.length > 0 && (
          <div>
            <p className="text-xs font-bold text-zinc-400 uppercase tracking-widest mb-3 flex items-center gap-2">
              <Share2 className="w-3.5 h-3.5" /> Platform Spread Order
            </p>
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-4 space-y-2">
              {platform_progression.map((step: any, i: number) => {
                const cp = getPlatform(step.platform);
                return (
                  <div key={step.platform} className="flex items-center gap-3">
                    <div className={`w-6 h-6 rounded-full ${cp.bg} border ${cp.border} flex items-center justify-center text-xs font-black ${cp.color}`}>{i + 1}</div>
                    <span className={`${cp.color} text-sm font-bold flex items-center gap-1.5`}>{cp.icon}{cp.label}</span>
                    <span className="text-xs text-zinc-400 ml-auto">{step.first_seen ? new Date(step.first_seen).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' }) : '—'}</span>
                    {i === 0 && <span className="text-xs px-2 py-0.5 bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 rounded-full">Origin</span>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Risk over time chart */}
        {risk_over_time.length > 0 && (
          <div>
            <p className="text-xs font-bold text-zinc-400 uppercase tracking-widest mb-3 flex items-center gap-2">
              <BarChart2 className="w-3.5 h-3.5" /> Risk Escalation Over Time
            </p>
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-4">
              <div className="flex items-end gap-1 h-24">
                {risk_over_time.slice(-36).map((r: any, i: number) => {
                  const riskPct = (r.avg_risk / Math.max(maxRisk, 1)) * 88;
                  const barColor = r.avg_risk >= 0.7 ? 'bg-rose-500' : r.avg_risk >= 0.4 ? 'bg-amber-500' : 'bg-emerald-500';
                  return (
                    <div key={i} className="flex flex-col items-center flex-1" title={`${r.hour}: Risk ${(r.avg_risk * 100).toFixed(0)}% (${r.post_count} posts)`}>
                      <div className={`w-full rounded-t ${barColor} hover:opacity-80 transition-opacity cursor-default`} style={{ height: `${riskPct}px` }} />
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between text-xs text-zinc-700 mt-2">
                <span>Earlier</span><span>Latest</span>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.94, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.94, y: 20 }}
        className={`relative w-full max-w-6xl max-h-[92vh] overflow-hidden rounded-3xl border bg-gradient-to-br ${plat.headerGrad} border-zinc-700/50 shadow-2xl flex flex-col`}
      >
        {/* Header */}
        <div className={`flex items-center justify-between px-8 py-5 border-b border-zinc-700/50 bg-gradient-to-r ${plat.headerGrad} flex-shrink-0`}>
          <div className="flex items-center gap-4">
            <div className={`p-2.5 ${plat.bg} border ${plat.border} rounded-xl ${plat.color}`}>{plat.icon}</div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-black text-white">Post Investigation</h2>
                <span className={`text-xs px-2 py-0.5 rounded-full border font-bold ${RISK_COLORS[post.riskLevel as RiskLevel]}`}>{post.riskLevel}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${plat.bg} ${plat.color} border ${plat.border}`}>{plat.label}</span>
              </div>
              <p className="text-xs text-zinc-400 mt-0.5">Post ID: #{post.id}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {post.url && (
              <a href={post.url} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-zinc-300 bg-zinc-800/60 hover:bg-zinc-700/60 border border-zinc-700 rounded-xl transition-colors">
                <ExternalLink className="w-3.5 h-3.5" /> View Original
              </a>
            )}
            <button onClick={onClose}
              className="p-2 text-zinc-400 hover:text-white bg-zinc-800/60 hover:bg-zinc-700 border border-zinc-700 rounded-xl transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tab navigation */}
        <div className="flex items-center gap-1 px-8 py-3 border-b border-zinc-800 bg-zinc-950/50 flex-shrink-0">
          {([
            { key: 'overview', label: 'Overview', icon: <FileText className="w-3.5 h-3.5" /> },
            { key: 'cross-channel', label: `Cross-Channel${crossData ? ` (${crossData.platform_count ?? 0} platforms)` : ''}`, icon: <Layers className="w-3.5 h-3.5" /> },
            { key: 'spread', label: `Track Spread${spreadData ? ` — ${spreadData.spread_label}` : ''}`, icon: <Share2 className="w-3.5 h-3.5" /> },
          ] as const).map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as typeof activeTab)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all ${
                activeTab === tab.key
                  ? `${plat.bg} ${plat.color} border ${plat.border}`
                  : 'text-zinc-400 hover:text-zinc-300 hover:bg-zinc-800/50'
              }`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="overflow-y-auto flex-1">
          {activeTab === 'overview' && (
            <div className="p-8 grid grid-cols-1 lg:grid-cols-5 gap-8">
              {/* ── Left: Content ── */}
              <div className="lg:col-span-3 space-y-6">
                {/* Author */}
                <div className="flex items-center gap-3">
                  <div className={`w-11 h-11 rounded-full ${plat.bg} border ${plat.border} flex items-center justify-center text-sm font-black ${plat.color}`}>
                    {initials(post.authorName)}
                  </div>
                  <div>
                    <p className="text-base font-bold text-white">{post.authorName}</p>
                    <div className="flex items-center gap-2 text-xs text-zinc-400 mt-0.5">
                      <Clock className="w-3 h-3" />
                      {new Date(post.postedAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                      <span className="text-zinc-700">•</span>
                      <User className="w-3 h-3" />
                      ID: {post.authorId}
                    </div>
                  </div>
                </div>

                {/* Media type indicator + actual media */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs font-bold text-zinc-400 uppercase tracking-widest">Content Type</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-bold uppercase ${
                      mediaType === 'video' ? 'bg-rose-500/15 text-rose-400' :
                      mediaType === 'audio' ? 'bg-violet-500/15 text-violet-400' :
                      mediaType === 'image' ? 'bg-cyan-500/15 text-cyan-400' :
                      'bg-zinc-700 text-zinc-400'
                    }`}>
                      {mediaType === 'video' ? '▶ Video' : mediaType === 'audio' ? '♪ Audio' : mediaType === 'image' ? '⬛ Image' : '📄 Text'}
                    </span>
                  </div>
                  <MediaBlock post={post} />
                </div>

                {/* Post text */}
                <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5">
                  <p className="text-xs font-bold text-zinc-400 uppercase tracking-widest mb-3">Post Content</p>
                  <p className="text-zinc-200 leading-relaxed text-base">{post.text}</p>
                  {post.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-zinc-800">
                      {post.tags.map((tag: string) => (
                        <span key={tag} className={`text-xs px-2 py-1 rounded-lg font-medium ${plat.bg} ${plat.color} border ${plat.border}`}>
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Engagement */}
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { icon: <ThumbsUp className="w-4 h-4" />, label: 'Engagement', value: post.engagement?.toLocaleString() || '0', color: 'text-blue-400' },
                    { icon: <Eye className="w-4 h-4" />, label: 'Est. Reach', value: Math.round((post.engagement || 0) * 4.2).toLocaleString(), color: 'text-cyan-400' },
                    { icon: <MessageCircle className="w-4 h-4" />, label: 'Virality', value: post.riskScore > 0.7 ? 'HIGH' : post.riskScore > 0.4 ? 'MED' : 'LOW', color: post.riskScore > 0.7 ? 'text-rose-400' : post.riskScore > 0.4 ? 'text-amber-400' : 'text-emerald-400' },
                  ].map(stat => (
                    <div key={stat.label} className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-3 text-center">
                      <div className={`flex justify-center mb-1 ${stat.color}`}>{stat.icon}</div>
                      <p className={`text-base font-bold ${stat.color}`}>{stat.value}</p>
                      <p className="text-xs text-zinc-400 mt-0.5">{stat.label}</p>
                    </div>
                  ))}
                </div>

                {/* Quick cross-channel preview */}
                {crossData && crossData.channels?.length > 0 && (
                  <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-xs font-bold text-zinc-400 uppercase tracking-widest flex items-center gap-2">
                        <Layers className="w-3.5 h-3.5" /> Found on {crossData.platform_count} Platforms
                      </p>
                      <button onClick={() => setActiveTab('cross-channel')} className="text-xs text-cyan-400 hover:text-cyan-300 font-bold flex items-center gap-1">
                        View All <ChevronRight className="w-3 h-3" />
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {crossData.channels.slice(0, 6).map((ch: any) => {
                        const cp = getPlatform(ch.platform);
                        return (
                          <span key={ch.platform} className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-xl font-bold ${cp.bg} ${cp.color} border ${cp.border}`}>
                            {cp.icon} {ch.mention_count} on {cp.label}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* ── Right: AI Analysis ── */}
              <div className="lg:col-span-2 space-y-5">
                <div>
                  <p className="text-xs font-bold text-zinc-400 uppercase tracking-widest mb-4">AI Intelligence Report</p>

                  <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5 space-y-5">
                    {scoreBar(post.riskScore, RISK_BAR[post.riskLevel as RiskLevel], 'Risk Score', `— ${post.riskLevel}`)}
                    {scoreBar(post.rumourProbability, 'bg-amber-500', 'Rumour Probability')}
                    {scoreBar(post.botScore, 'bg-yellow-500', 'Bot Score', post.botScore > 0.6 ? '— Likely Automated' : undefined)}
                    <div className="w-full h-px bg-zinc-800" />
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-zinc-800/60 rounded-xl p-3">
                        <p className="text-xs text-zinc-400 mb-1">Sentiment</p>
                        <p className={`text-sm font-bold ${post.sentiment === 'NEGATIVE' ? 'text-rose-400' : post.sentiment === 'POSITIVE' ? 'text-emerald-400' : 'text-zinc-300'}`}>
                          {post.sentiment}
                        </p>
                      </div>
                      <div className="bg-zinc-800/60 rounded-xl p-3">
                        <p className="text-xs text-zinc-400 mb-1">Sentiment Score</p>
                        <p className="text-sm font-bold text-white">{(post.sentimentScore * 100).toFixed(1)}%</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Threat summary */}
                <div className={`border rounded-2xl p-4 ${post.riskScore > 0.7 ? 'bg-rose-500/5 border-rose-500/30' : post.riskScore > 0.4 ? 'bg-amber-500/5 border-amber-500/30' : 'bg-emerald-500/5 border-emerald-500/30'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className={`w-4 h-4 ${post.riskScore > 0.7 ? 'text-rose-400' : post.riskScore > 0.4 ? 'text-amber-400' : 'text-emerald-400'}`} />
                    <p className={`text-sm font-bold ${post.riskScore > 0.7 ? 'text-rose-300' : post.riskScore > 0.4 ? 'text-amber-300' : 'text-emerald-300'}`}>
                      Threat Assessment
                    </p>
                  </div>
                  <p className="text-xs text-zinc-400 leading-relaxed">
                    {post.riskScore > 0.8
                      ? 'HIGH PRIORITY — This post exhibits strong misinformation signals including elevated bot amplification and viral spread patterns. Immediate action recommended.'
                      : post.riskScore > 0.6
                      ? 'ELEVATED RISK — Post shows suspicious engagement patterns and unverified claims. Manual review advised before any official response.'
                      : post.riskScore > 0.4
                      ? 'MODERATE RISK — Claims require verification. Monitor for spread velocity over next 2 hours.'
                      : 'LOW RISK — Post appears credible. Continue passive monitoring.'}
                  </p>
                </div>

                {/* Spread preview */}
                {spreadData && (
                  <div className={`border rounded-2xl p-4 cursor-pointer hover:opacity-90 transition-opacity ${spreadData.is_viral ? 'bg-rose-500/5 border-rose-500/30' : 'bg-zinc-900/50 border-zinc-800'}`}
                    onClick={() => setActiveTab('spread')}>
                    <div className="flex items-center justify-between mb-2">
                      <p className={`text-sm font-bold flex items-center gap-2 ${spreadData.is_viral ? 'text-rose-300' : 'text-zinc-300'}`}>
                        <Share2 className="w-4 h-4" /> Spread Analysis
                      </p>
                      <span className={`text-xs px-2 py-0.5 rounded-full border font-black ${spreadData.is_viral ? 'bg-rose-500/10 text-rose-400 border-rose-500/30' : 'bg-zinc-800 text-zinc-400 border-zinc-700'}`}>
                        {spreadData.spread_label}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-zinc-400">
                      <span><span className="text-white font-bold">{spreadData.spread_score}</span>/100 score</span>
                      <span><span className="text-white font-bold">{spreadData.velocity_per_hour}</span>/hr velocity</span>
                      <span className="ml-auto text-cyan-400 font-bold flex items-center gap-1">Details <ChevronRight className="w-3 h-3" /></span>
                    </div>
                  </div>
                )}

                {/* Source chain */}
                <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-4">
                  <p className="text-xs font-bold text-zinc-400 uppercase tracking-widest mb-3">Source Chain</p>
                  <div className="flex flex-col gap-2">
                    {[
                      { label: 'Platform', value: plat.label, icon: plat.icon },
                      { label: 'Author', value: post.authorName, icon: <User className="w-3.5 h-3.5" /> },
                      { label: 'Post Time', value: new Date(post.postedAt).toLocaleTimeString(), icon: <Clock className="w-3.5 h-3.5" /> },
                      { label: 'Content Type', value: mediaType.charAt(0).toUpperCase() + mediaType.slice(1), icon: <FileText className="w-3.5 h-3.5" /> },
                    ].map(row => (
                      <div key={row.label} className="flex items-center gap-2 text-xs">
                        <span className="text-zinc-400 w-4">{row.icon}</span>
                        <span className="text-zinc-400 w-20 flex-shrink-0">{row.label}</span>
                        <span className="text-zinc-200 font-medium truncate">{row.value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Action buttons */}
                <div className="space-y-2">
                  <button className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 hover:opacity-90 text-white rounded-xl font-bold text-sm transition-all shadow-lg shadow-cyan-900/30">
                    <CheckCircle className="w-4 h-4" /> Mark as Verified
                  </button>
                  <button className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/30 text-rose-400 rounded-xl font-bold text-sm transition-all">
                    <Flag className="w-4 h-4" /> Flag as Misinformation
                  </button>
                  <button
                    onClick={() => setActiveTab('spread')}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-violet-500/10 hover:bg-violet-500/20 border border-violet-500/30 text-violet-300 rounded-xl font-bold text-sm transition-all"
                  >
                    <Share2 className="w-4 h-4" /> Track Spread
                    {spreadData && <span className="text-xs font-black text-violet-400">{spreadData.spread_label}</span>}
                  </button>
                  <button
                    onClick={() => setActiveTab('cross-channel')}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-zinc-800/50 hover:bg-zinc-700/50 border border-zinc-700 text-zinc-300 rounded-xl font-bold text-sm transition-all"
                  >
                    <Layers className="w-4 h-4" /> Cross-Channel View
                    {crossData && <span className="text-xs font-black text-cyan-400">{crossData.platform_count} platforms</span>}
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'cross-channel' && <CrossChannelPanel />}
          {activeTab === 'spread' && <SpreadTrackerPanel />}
        </div>
      </motion.div>
    </motion.div>
  );
};

// ─── Post Card (list item) ────────────────────────────────────────────────────
const PostCard: React.FC<{ post: any; active: boolean; onClick: () => void; onInvestigate: () => void }> = ({
  post, active, onClick, onInvestigate,
}) => {
  const plat = getPlatform(post.platform);
  const mediaType = detectMediaType(post);
  const mediaIcon = mediaType === 'video' ? <Film className="w-3 h-3" /> : mediaType === 'audio' ? <Mic className="w-3 h-3" /> : mediaType === 'image' ? <Image className="w-3 h-3" /> : <FileText className="w-3 h-3" />;

  return (
    <div
      onClick={onClick}
      className={`p-4 rounded-2xl border cursor-pointer transition-all duration-200 group ${
        active
          ? `${plat.bg} ${plat.border}`
          : 'bg-zinc-800/30 border-zinc-700/40 hover:bg-zinc-800/60 hover:border-zinc-600/50'
      }`}
    >
      {/* Platform + media type */}
      <div className="flex items-center justify-between mb-2">
        <div className={`flex items-center gap-1.5 text-xs font-bold ${plat.color}`}>
          {plat.icon} {plat.label}
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-zinc-400">{mediaIcon}</span>
          <span className={`text-xs px-2 py-0.5 rounded-full border font-bold ${RISK_COLORS[post.riskLevel as RiskLevel]}`}>
            {post.riskLevel}
          </span>
        </div>
      </div>

      {/* Author */}
      <p className="text-xs text-zinc-400 mb-1.5">{post.authorName}</p>

      {/* Text */}
      <p className="text-sm text-zinc-300 line-clamp-2 mb-3">{post.text}</p>

      {/* Footer */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-zinc-400">
          <span>{(post.riskScore * 100).toFixed(0)}% risk</span>
          <span>·</span>
          <span>{post.engagement?.toLocaleString()} eng</span>
        </div>
        <button
          onClick={e => { e.stopPropagation(); onInvestigate(); }}
          className={`flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-all ${plat.bg} ${plat.color} border ${plat.border}`}
        >
          Investigate <ZoomIn className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────
interface PostInvestigationProps {
  monitoringScope?: MonitoringScope;
}

export const PostInvestigation: React.FC<PostInvestigationProps> = ({ monitoringScope }) => {
  const [searchText, setSearchText] = useState('');
  const [platform, setPlatform] = useState<string>('');
  const [riskFilter, setRiskFilter] = useState<string>('ALL');
  const [sortBy, setSortBy] = useState<'risk' | 'engagement' | 'newest'>('risk');
  const [posts, setPosts] = useState<any[]>(MOCK_POSTS);
  const [selectedPost, setSelectedPost] = useState<any>(MOCK_POSTS[0]);
  const [investigatePost, setInvestigatePost] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(MOCK_POSTS.length);
  const [page, setPage] = useState(1);
  const pageSize = 20;

  // Derived KPIs
  const kpis = React.useMemo(() => {
    const critical = posts.filter(p => p.riskLevel === 'CRITICAL' || p.riskLevel === 'HIGH').length;
    const botAmp = posts.filter(p => p.botScore > 0.5).length;
    const avgRisk = posts.length > 0 ? posts.reduce((s, p) => s + p.riskScore, 0) / posts.length : 0;
    return { total: posts.length, critical, botAmp, avgRisk };
  }, [posts]);

  // Filtered + sorted view
  const displayPosts = React.useMemo(() => {
    let list = posts;
    if (riskFilter !== 'ALL') list = list.filter(p => p.riskLevel === riskFilter);
    if (sortBy === 'risk') list = [...list].sort((a, b) => b.riskScore - a.riskScore);
    else if (sortBy === 'engagement') list = [...list].sort((a, b) => (b.engagement || 0) - (a.engagement || 0));
    else list = [...list].sort((a, b) => new Date(b.postedAt).getTime() - new Date(a.postedAt).getTime());
    return list;
  }, [posts, riskFilter, sortBy]);

  const exportCSV = () => {
    const headers = ['ID', 'Platform', 'Author', 'Risk Score', 'Risk Level', 'Bot Score', 'Engagement', 'Posted At', 'Text'];
    const rows = displayPosts.map(p => [p.id, p.platform, p.authorName, (p.riskScore * 100).toFixed(1) + '%', p.riskLevel, (p.botScore * 100).toFixed(1) + '%', p.engagement, p.postedAt, `"${(p.text || '').replace(/"/g, '""')}"`]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'post_investigation_export.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  // Auto-load real posts on mount
  useEffect(() => {
    const normPost = (p: any) => {
      const score: number = typeof p.sentiment === 'number' ? p.sentiment : 0;
      return {
        id: String(p.id), platform: p.platform || 'unknown', text: p.text || '',
        authorName: p.author || 'Unknown', authorId: String(p.author_id || p.id),
        riskScore: typeof p.risk_score === 'number' ? p.risk_score : 0,
        riskLevel: getRiskLevel(typeof p.risk_score === 'number' ? p.risk_score : 0),
        rumourProbability: typeof p.bot_probability === 'number' ? p.bot_probability : 0,
        engagement: (p.likes || 0) + (p.comments || 0),
        postedAt: p.created_at || new Date().toISOString(),
        tags: p.tags || [], sentiment: score > 0.1 ? 'POSITIVE' : score < -0.1 ? 'NEGATIVE' : 'NEUTRAL',
        sentimentScore: score, botScore: typeof p.bot_probability === 'number' ? p.bot_probability : 0,
        url: p.url, image_url: p.image_url,
      };
    };
    setLoading(true);
    apiService.posts.getPosts(1, pageSize, undefined, monitoringScope)
      .then(response => {
        const raw: any[] = Array.isArray(response) ? response : (response as any).items ?? [];
        const normalized = raw.map(normPost);
        if (normalized.length > 0) {
          setPosts(normalized);
          setTotal((response as any).total ?? normalized.length);
          setSelectedPost(normalized[0]);
        }
      })
      .catch(() => { /* keep mock data */ })
      .finally(() => setLoading(false));
  }, [pageSize, monitoringScope]);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await apiService.posts.getPosts(page, pageSize, {
        searchText, platform: platform ? [platform] : undefined,
      }, monitoringScope);
      const raw: any[] = Array.isArray(response) ? response : (response as any).items ?? [];
      const normalized = raw.map((p: any) => {
        const score: number = typeof p.sentiment === 'number' ? p.sentiment : 0;
        return {
          id: String(p.id), platform: p.platform || 'unknown', text: p.text || '',
          authorName: p.author || 'Unknown', authorId: String(p.author_id || p.id),
          riskScore: typeof p.risk_score === 'number' ? p.risk_score : 0,
          riskLevel: getRiskLevel(typeof p.risk_score === 'number' ? p.risk_score : 0),
          rumourProbability: typeof p.bot_probability === 'number' ? p.bot_probability : 0,
          engagement: (p.likes || 0) + (p.comments || 0),
          postedAt: p.created_at || new Date().toISOString(),
          tags: p.tags || [], sentiment: score > 0.1 ? 'POSITIVE' : score < -0.1 ? 'NEGATIVE' : 'NEUTRAL',
          sentimentScore: score, botScore: typeof p.bot_probability === 'number' ? p.bot_probability : 0,
          url: p.url, image_url: p.image_url,
        };
      });
      if (normalized.length > 0) {
        setPosts(normalized);
        setTotal((response as any).total ?? normalized.length);
        setSelectedPost(normalized[0]);
      }
    } catch {
      // keep mock data
    } finally {
      setLoading(false);
    }
  };

  const plat = selectedPost ? getPlatform(selectedPost.platform) : null;
  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="min-h-screen pb-10">
      {/* Header */}
      <div className="mb-5">
        <h1 className="text-3xl font-bold text-white flex items-center gap-3">
          <div className="p-2.5 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-xl border border-cyan-500/20">
            <Search className="w-6 h-6 text-cyan-400" />
          </div>
          Post Investigation
        </h1>
        <p className="text-zinc-400 text-base mt-1.5">
          Deep-dive analysis of individual posts — text, video, audio, platform, and AI risk signals
        </p>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
        {[
          { label: 'Total Posts', value: kpis.total, icon: <FileText className="w-5 h-5" />, color: 'text-cyan-400', bg: 'bg-cyan-500/10 border-cyan-500/20' },
          { label: 'Critical / High Risk', value: kpis.critical, icon: <AlertTriangle className="w-5 h-5" />, color: 'text-rose-400', bg: 'bg-rose-500/10 border-rose-500/20' },
          { label: 'Bot-Amplified', value: kpis.botAmp, icon: <Activity className="w-5 h-5" />, color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20' },
          { label: 'Avg Risk Score', value: `${(kpis.avgRisk * 100).toFixed(1)}%`, icon: <TrendingUp className="w-5 h-5" />, color: 'text-violet-400', bg: 'bg-violet-500/10 border-violet-500/20' },
        ].map(k => (
          <div key={k.label} className={`rounded-xl border ${k.bg} p-4 flex items-center gap-3`}>
            <div className={k.color}>{k.icon}</div>
            <div>
              <p className={`text-2xl font-black ${k.color}`}>{k.value}</p>
              <p className="text-xs text-zinc-400 mt-0.5">{k.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="glass-panel rounded-2xl p-5 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-zinc-400 mb-2">Search Text</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <input
                type="text" value={searchText} onChange={e => setSearchText(e.target.value)}
                placeholder="Keywords, hashtags, URLs..."
                className="w-full pl-9 pr-4 py-2.5 bg-zinc-900/60 border border-zinc-700 text-white placeholder-zinc-500 rounded-xl focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 outline-none transition-all"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">Platform</label>
            <select value={platform} onChange={e => setPlatform(e.target.value)}
              className="w-full px-4 py-2.5 bg-zinc-900/60 border border-zinc-700 text-white rounded-xl focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 outline-none transition-all">
              <option className="bg-zinc-900" value="">All Platforms</option>
              {Object.entries(PLATFORM_CONFIG).map(([key, cfg]) => (
                <option key={key} className="bg-zinc-900" value={key}>{cfg.label}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button type="submit" disabled={loading}
              className="w-full px-4 py-2.5 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 disabled:opacity-50 text-white rounded-xl font-bold transition-all shadow-lg shadow-cyan-900/30">
              {loading ? 'Searching…' : 'Search Posts'}
            </button>
          </div>
        </div>
      </form>

      {/* Risk filter + sort + export row */}
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <span className="text-xs text-zinc-400 font-semibold">Risk:</span>
        {['ALL', 'CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'MINIMAL'].map(lvl => (
          <button key={lvl} onClick={() => setRiskFilter(lvl)}
            className={`px-2.5 py-1 rounded-lg text-xs font-bold border transition-colors ${riskFilter === lvl
              ? lvl === 'CRITICAL' ? 'bg-rose-500/20 text-rose-300 border-rose-500/50'
              : lvl === 'HIGH' ? 'bg-orange-500/20 text-orange-300 border-orange-500/50'
              : lvl === 'MEDIUM' ? 'bg-amber-500/20 text-amber-300 border-amber-500/50'
              : lvl === 'LOW' ? 'bg-blue-500/20 text-blue-300 border-blue-500/50'
              : lvl === 'MINIMAL' ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/50'
              : 'bg-zinc-700 text-white border-zinc-600'
              : 'text-zinc-400 border-zinc-700/50 bg-zinc-800/50 hover:bg-zinc-700/50'}`}>
            {lvl}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-zinc-400">Sort:</span>
          <select value={sortBy} onChange={e => setSortBy(e.target.value as typeof sortBy)}
            className="text-xs bg-zinc-800/60 border border-zinc-700/50 text-zinc-200 rounded-lg px-2 py-1.5 outline-none focus:ring-1 focus:ring-cyan-500/50">
            <option value="risk">Highest Risk</option>
            <option value="engagement">Most Engagement</option>
            <option value="newest">Newest First</option>
          </select>
          <button onClick={exportCSV}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-900/40 hover:bg-emerald-800/50 text-emerald-400 rounded-lg text-xs font-medium transition-colors border border-emerald-700/40">
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
        </div>
      </div>

      {/* Main layout */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Post list */}
        <div className="lg:col-span-2">
          <div className="glass-panel rounded-2xl p-4">
            <div className="flex items-center justify-between mb-4 px-1">
              <p className="text-base font-bold text-zinc-200">Results</p>
              <span className="text-sm text-zinc-400">{displayPosts.length}{displayPosts.length !== total ? ` / ${total}` : ''} posts</span>
            </div>
            {loading ? (
              <div className="space-y-3">
                {[1,2,3].map(i => <div key={i} className="h-24 bg-zinc-800/50 animate-pulse rounded-2xl" />)}
              </div>
            ) : (
              <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
                {displayPosts.map(post => (
                  <PostCard
                    key={post.id}
                    post={post}
                    active={selectedPost?.id === post.id}
                    onClick={() => setSelectedPost(post)}
                    onInvestigate={() => setInvestigatePost(post)}
                  />
                ))}
              </div>
            )}
            {totalPages > 1 && (
              <div className="mt-4 flex gap-1 justify-center">
                <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}
                  className="px-3 py-1.5 text-xs border border-zinc-700 text-zinc-400 rounded-lg disabled:opacity-40 hover:border-zinc-500">← Prev</button>
                <span className="px-3 py-1.5 text-xs text-zinc-400">{page}/{totalPages}</span>
                <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages}
                  className="px-3 py-1.5 text-xs border border-zinc-700 text-zinc-400 rounded-lg disabled:opacity-40 hover:border-zinc-500">Next →</button>
              </div>
            )}
          </div>
        </div>

        {/* Detail panel */}
        <div className="lg:col-span-3">
          {selectedPost && plat ? (
            <div className="glass-panel rounded-2xl overflow-hidden">
              {/* Platform banner */}
              <div className={`bg-gradient-to-r ${plat.headerGrad} px-6 py-4 flex items-center justify-between border-b border-zinc-800/60`}>
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-xl ${plat.bg} border ${plat.border}`}>
                    <span className={plat.color}>{plat.icon}</span>
                  </div>
                  <div>
                    <p className={`text-sm font-bold ${plat.color} uppercase tracking-widest`}>{plat.label}</p>
                    <p className="text-xs text-zinc-400">{selectedPost.authorName} · {new Date(selectedPost.postedAt).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}</p>
                  </div>
                </div>
                <button
                  onClick={() => setInvestigatePost(selectedPost)}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-bold ${plat.bg} ${plat.color} border ${plat.border} rounded-xl hover:opacity-80 transition-opacity`}
                >
                  <ZoomIn className="w-4 h-4" /> Full Investigation
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Media */}
                <MediaBlock post={selectedPost} />

                {/* Text */}
                <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5">
                  <p className="text-zinc-200 leading-relaxed text-base">{selectedPost.text}</p>
                  {selectedPost.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-zinc-800">
                      {selectedPost.tags.map((tag: string) => (
                        <span key={tag} className={`text-xs px-2 py-1 rounded-lg font-medium ${plat.bg} ${plat.color} border ${plat.border}`}>
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Stats grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { label: 'Risk Score', value: `${(selectedPost.riskScore*100).toFixed(0)}%`, sub: selectedPost.riskLevel, color: RISK_BAR[selectedPost.riskLevel as RiskLevel].replace('bg-', 'text-') },
                    { label: 'Bot Score', value: `${(selectedPost.botScore*100).toFixed(0)}%`, sub: selectedPost.botScore > 0.6 ? 'Automated' : 'Likely Human', color: 'text-yellow-400' },
                    { label: 'Rumour Prob.', value: `${(selectedPost.rumourProbability*100).toFixed(0)}%`, sub: 'AI assessed', color: 'text-amber-400' },
                    { label: 'Engagement', value: selectedPost.engagement?.toLocaleString() || '0', sub: 'interactions', color: 'text-cyan-400' },
                  ].map(s => (
                    <div key={s.label} className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-3 text-center">
                      <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
                      <p className="text-xs text-zinc-400 mt-0.5">{s.label}</p>
                      <p className="text-xs text-zinc-400">{s.sub}</p>
                    </div>
                  ))}
                </div>

                {/* Risk bar */}
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm font-medium text-zinc-300">Overall Risk</span>
                    <span className={`text-sm font-bold px-2 py-0.5 rounded-full border ${RISK_COLORS[selectedPost.riskLevel as RiskLevel]}`}>
                      {selectedPost.riskLevel}
                    </span>
                  </div>
                  <div className="w-full bg-zinc-800 rounded-full h-3">
                    <div className={`h-3 rounded-full ${RISK_BAR[selectedPost.riskLevel as RiskLevel]} transition-all duration-700`}
                      style={{ width: `${selectedPost.riskScore * 100}%` }} />
                  </div>
                </div>

                {/* CTA */}
                <button
                  onClick={() => setInvestigatePost(selectedPost)}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 hover:opacity-90 text-white rounded-xl font-bold text-base transition-all shadow-lg shadow-cyan-900/30"
                >
                  <ZoomIn className="w-5 h-5" /> Open Full Investigation Report
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          ) : (
            <div className="glass-panel rounded-2xl p-16 text-center">
              <div className="p-5 bg-cyan-500/10 rounded-2xl border border-cyan-500/20 w-fit mx-auto mb-4">
                <Search className="w-10 h-10 text-cyan-400" />
              </div>
              <p className="text-zinc-200 text-lg font-semibold">Select a post to investigate</p>
              <p className="text-zinc-400 text-sm mt-2">Click any post on the left — see media, text, platform details, and AI analysis</p>
            </div>
          )}
        </div>
      </div>

      {/* Full Investigation Modal */}
      <AnimatePresence>
        {investigatePost && (
          <InvestigationModal post={investigatePost} onClose={() => setInvestigatePost(null)} />
        )}
      </AnimatePresence>
    </div>
  );
};

export default PostInvestigation;
